import { get } from "svelte/store";
import { updateSession, sessionStore, type Session, NavigationType } from "../stores/session";
import { getSearchParam, removeSearchParam } from "./document";
import { decodeAndValidate, validateHandle, updateJWT, identifyJWT, JWTFlavour } from "./auth/jwt";
import { getAuthFlow, waitForAnonymousStart } from "./auth/auth";
import { settingsStore } from "../stores/settings";
import { isDemoMode, isLiveMode } from "../stores/mode";
import { navigateToPath, navigateTo } from "../routing";
import { createSystemRoutes, validateCampaign } from "../routing";
import PATHS from "../constants/routePaths";
import { getUser } from "./userManager";
import { trackSiteVisit } from "../apiLoader/track";
import { SecondaryAuthFlow } from "./auth/SecondaryAuthFlow";
import { callOfDutyStart } from "../apiLoader/listener";
import { withCaptcha, CaptchaError } from "./auth/captchaAuth";
import { decodeJWT } from "../public/lib/helpers/jwt";

const startAuthFlow = async () => {
  const session = get(sessionStore);
  let channel = session.foojiData?.campaign_user?.channel;

  if (!channel) {
    const availableChannels = get(settingsStore).channels ?? [];
    if (availableChannels.includes("anonymous")) {
      channel = "anonymous";
    } else {
      console.error("No channel available in settings or fooji data");
      navigateToPath(PATHS.INVALID_CHANNEL);
      return;
    }
  }

  const authFlow = getAuthFlow(channel);
  await authFlow.start({ channel, foojiData: session.foojiData });
};

export const checkAuth = async () => {
  const session = get(sessionStore);
  const { jwt } = session;
  const isValid = jwt && decodeAndValidate(jwt);
  if (!isValid) {
    try {
      get(settingsStore).requiresCaptcha ? await withCaptcha(startAuthFlow) : await startAuthFlow();
    } catch (e) {
      console.error("Error starting auth flow", e);

      if (!(e instanceof CaptchaError)) {
        navigateToPath(PATHS.AUTHENTICATION_ERROR);
      }
    }
  }
};

const navigateBack = (session: Session, routeName: string) => {
  if (!["/", session.currentPath].includes(routeName)) {
    navigateToPath(session.currentPath ?? "/");
  }
};

export const checkAllowlist = (routeName: string) => {
  const session = get(sessionStore);
  const settings = get(settingsStore);
  const systemRoutes = createSystemRoutes(settings);
  const stepAllowList: string[] = session?.stepAllowList ?? [];

  const route = [...session.routes, ...systemRoutes].find((route) => route.path === routeName);

  if (!route) return false;

  if (!route.public && !stepAllowList.includes(routeName)) {
    console.error(`Route ${routeName} is not in the allowlist`);
    navigateBack(session, routeName);
    return false;
  }
  return true;
};

const finishCallOfDutyAuth = async () => {
  try {
    const response = await callOfDutyStart({ workflow_key: get(settingsStore).campaign as string });
    const processKey = response.process_key;

    if (!processKey) {
      throw new Error("No process key returned from internal call of duty listener");
    }

    await waitForAnonymousStart();

    navigateTo(NavigationType.Next);
  } catch (e) {
    console.error("Error finishing call of duty auth", e);
    navigateToPath(PATHS.AUTHENTICATION_ERROR);
  }
};

export const JWTParamHelper = {
  handleJWTParamAuth: async (routeName: string) => {
    const jwt = getSearchParam("jwt");

    if (jwt) {
      const jwtData = decodeJWT(jwt);

      // We want to make sure temporary JWTs are not processed here
      // They need to get processed in handleTempJWTIfPresent, and that now happens after turnstile, so after this
      if (jwtData.is_temp) {
        return;
      }

      if (get(settingsStore).secondary_channels?.length && get(sessionStore).isAuthenticating) {
        return await new SecondaryAuthFlow().finish();
      }

      const flavour = identifyJWT(jwtData);
      const channels = get(settingsStore).channels;
      const isCallOfDutyChannel = channels?.length === 1 && channels?.[0] === "callofduty";
    
      if (flavour === JWTFlavour.MICROSITE && !isCallOfDutyChannel) {
        try {
          await validateHandle(jwt);
        } catch (e) {
          console.error("Error validating identifier", e);
          return navigateToPath(PATHS.AUTHENTICATION_ERROR);
        }
      }

      updateJWT(jwt);

      if (isCallOfDutyChannel && routeName === PATHS.COD_AUTH) {
        await finishCallOfDutyAuth();
      }

      if (flavour === JWTFlavour.MICROSITE) {
        trackSiteVisit();
      }

      removeSearchParam("jwt");
    }
  }
};

export const routeChangeHandler = async (routeName: string) => {
  if (isLiveMode() && !validateCampaign()) return;
  if (!isDemoMode() && !checkAllowlist(routeName)) return;

  updateSession({ currentPath: routeName });

  await JWTParamHelper.handleJWTParamAuth(routeName);

  const session = get(sessionStore);
  const currentRoute = session.routes.find((route) => route.path === routeName);

  if (currentRoute && currentRoute.requiresAuth) {
    if (isDemoMode()) {
      await getUser();
    } else {
      void (async () => {
        await checkAuth();

        // We need to get the session store again, because it has probably been updated at this point
        // We need to prevent a call if we're going to error out, since this will cause a navigation to /error
        // At this point we have probably handled the authentication error
        // e.g. if a test link has expired, we will go to the /link-expired page, but this call trigger a nav to /error
        get(sessionStore).jwt?.length && (await getUser());
      })();
    }
  }
};
